// ====================================================================
// File: src/articles/HowToPassJamb.jsx
// Article: How to Pass JAMB 2025 – Complete Strategy Guide
// (Long, SEO-Optimized, AdSense-Ready)
// ====================================================================

import { Typography, Divider } from "@mui/material";


export default function HowToPassJamb() {
  return (
    <>
      {/* INTRO */}
      <Typography sx={{ mb: 2 }}>
        Passing JAMB is not about reading for long hours — it is about reading
        the **right way**, understanding the **exam pattern**, and practicing
        strategically. JAMB UTME is a Computer-Based Test (CBT), and students who
        practice CBT-style questions consistently perform far better.
      </Typography>

      <Typography sx={{ mb: 3 }}>
        This complete guide provides a structured, proven strategy used by
        thousands of high-scoring students who achieved **250–320+** in past
        JAMB exams. Follow the principles carefully and you will drastically
        improve your score.
      </Typography>

      <Divider sx={{ my: 3 }} />

      {/* SECTION 1 */}
      <Typography variant="h4" fontWeight={700} sx={{ mt: 3 }}>
        1. Understand the JAMB Exam Structure
      </Typography>

      <Typography sx={{ mt: 2, mb: 2 }}>
        JAMB tests four subjects: **English Language** (compulsory) plus **three
        subjects related to your course**. Each subject has 40–60 questions,
        totaling about **180–200 questions**.
      </Typography>

      <Typography sx={{ mb: 3 }}>
        Time is approximately **120 minutes**, meaning you have less than **40
        seconds** per question. This is why speed and familiarity with
        CBT navigation are critical.
      </Typography>

      {/* SECTION 2 */}
      <Typography variant="h4" fontWeight={700} sx={{ mt: 3 }}>
        2. Choose the Correct Subject Combination
      </Typography>

      <Typography sx={{ mt: 2 }}>
        Many students lose admission not because of low scores but due to wrong
        subject combinations. Always check JAMB’s official brochure or use a
        verified subject combination guide.
      </Typography>

      <Typography sx={{ mt: 1 }}>
        Examples:
      </Typography>

      <Typography component="ul" sx={{ pl: 3, mb: 3 }}>
        <li>Medicine & Surgery → English, Biology, Chemistry, Physics</li>
        <li>Engineering → English, Mathematics, Physics, Chemistry</li>
        <li>Law → English, Literature, Government, CRS/IRS</li>
        <li>Accounting → English, Mathematics, Economics, Government</li>
      </Typography>

      {/* SECTION 3 */}
      <Typography variant="h4" fontWeight={700} sx={{ mt: 3 }}>
        3. Study High-Yield Topics First
      </Typography>

      <Typography sx={{ mt: 2 }}>
        JAMB repeats certain topics every year. Focus on these first before
        reading the entire syllabus.
      </Typography>

      <Typography sx={{ mt: 2, mb: 2, fontWeight: 700 }}>
        High-Yield Topics:
      </Typography>

      <Typography component="ul" sx={{ pl: 3, mb: 3 }}>
        <li>English → Comprehension, Lexis & Structure, Oral English</li>
        <li>Mathematics → Algebra, Indices, Trigonometry, Word Problems</li>
        <li>Physics → Motion, Electricity, Waves, Optics</li>
        <li>Chemistry → Stoichiometry, Organic Chemistry, Acids & Bases</li>
        <li>Biology → Genetics, Ecology, Cell Structure</li>
      </Typography>

      {/* SECTION 4 */}
      <Typography variant="h4" fontWeight={700} sx={{ mt: 3 }}>
        4. Create a Weekly Study Timetable
      </Typography>

      <Typography sx={{ mt: 2 }}>
        You cannot read “everything”. A structured timetable helps you study at a
        realistic pace.
      </Typography>

      <Typography component="ul" sx={{ pl: 3, mb: 3 }}>
        <li>Study 2 subjects daily (weakest first)</li>
        <li>Practice 40–60 CBT questions per day</li>
        <li>Write one full mock exam every Saturday</li>
        <li>Revise errors every Sunday</li>
      </Typography>

      {/* SECTION 5 */}
      <Typography variant="h4" fontWeight={700} sx={{ mt: 3 }}>
        5. Practice JAMB Past Questions the Right Way
      </Typography>

      <Typography sx={{ mt: 2 }}>
        Don’t just read answers — answer questions under exam conditions:
      </Typography>

      <Typography component="ul" sx={{ pl: 3, mb: 3 }}>
        <li>Use a timer</li>
        <li>Don’t guess randomly (negative impact)</li>
        <li>Review skipped or wrong answers immediately</li>
        <li>Track weak topics and repeat them</li>
      </Typography>

      <Typography sx={{ mt: 2 }}>
        Consistency is what moves students from **180 → 250 → 300+**.
      </Typography>

      {/* SECTION 6 */}
      <Typography variant="h4" fontWeight={700} sx={{ mt: 3 }}>
        6. Improve Your Reading Speed
      </Typography>

      <Typography sx={{ mt: 2, mb: 3 }}>
        English passages consume time. Practice reading articles daily to improve
        comprehension speed. Faster reading = more time for calculations.
      </Typography>

      {/* SECTION 7 */}
      <Typography variant="h4" fontWeight={700} sx={{ mt: 3 }}>
        7. Use Smart Exam-Day Strategies
      </Typography>

      <Typography component="ul" sx={{ pl: 3, mb: 3 }}>
        <li>Start with your best subject</li>
        <li>Don’t spend more than 40 seconds per question</li>
        <li>Skip tough questions and return later</li>
        <li>Use elimination technique when unsure</li>
        <li>Don’t panic when the timer counts down</li>
      </Typography>

      {/* SECTION 8 */}
      <Typography variant="h4" fontWeight={700} sx={{ mt: 3 }}>
        8. Avoid These Common Mistakes
      </Typography>

      <Typography component="ul" sx={{ pl: 3, mb: 3 }}>
        <li>Reading only solved answers without attempting questions</li>
        <li>Ignoring English Language (it determines your total score)</li>
        <li>Studying only your favorite subjects</li>
        <li>Using outdated textbooks</li>
        <li>Not practicing CBT format early enough</li>
      </Typography>

      {/* SECTION 9 */}
      <Typography variant="h4" fontWeight={700} sx={{ mt: 3 }}>
        9. Recommended JAMB Textbooks
      </Typography>

      <Typography component="ul" sx={{ pl: 3, mb: 3 }}>
        <li>New General Mathematics – M.A. Agwu</li>
        <li>Essential Physics – Charles Ingram</li>
        <li>New School Chemistry – Osei Yaw Ababio</li>
        <li>Invisible Teachers (for English)</li>
      </Typography>

      {/* SECTION 10 */}
      <Typography variant="h4" fontWeight={700} sx={{ mt: 3 }}>
        10. Take CBT Mock Exams Frequently
      </Typography>

      <Typography sx={{ mb: 3 }}>
        JAMB CBT requires speed, accuracy, and familiarity with computer
        navigation. Weekly mock exams help you adapt to real-exam pressure and
        improve speed drastically.
      </Typography>

      <Divider sx={{ my: 4 }} />

      {/* ENDING */}
      <Typography sx={{ mt: 2 }}>
        Scoring high in JAMB is not magic — it is a result of a smart strategy,
        disciplined practice, and understanding what JAMB expects from you.
        Follow these steps consistently and you will see massive improvement.
      </Typography>

      <Typography sx={{ mt: 1, mb: 5 }}>
        Ready to practice? Start free JAMB CBT practice on CBT Master.
      </Typography>
	 
    </>
  );
}
